/**
 * Privacy-first analytics — the shared, pure part (used by the browser collector and the server).
 *
 * A visit sends one cumulative *snapshot* (re-sent as it grows). It holds counts and coarse
 * context only: no task text, no notes, no exact timestamps of the visitor's actions, no
 * identifiers. `sanitizeSnapshot` is the single gate on the server: anything not listed here is
 * dropped, every number is clamped, and free-form keys are restricted.
 */

export interface SnapshotEnv {
  /** Primary language subtag, e.g. `en`. */
  lang: string;
  /** Coarse viewport bucket, e.g. `≤480`, `≤1024`, `>1440`. */
  screen: string;
  theme: 'light' | 'dark' | '';
  /** Running as an installed app (display-mode: standalone). */
  standalone: boolean;
  /** Referring site's domain only (never the full URL); empty for direct/internal. */
  ref: string;
  /** `utm_source` of the landing URL, if any. */
  utm: string;
}

export interface Snapshot {
  v: 1;
  /** Random id for this page load only (not stored on the device, not reused). */
  sid: string;
  /** The visitor allowed "remember this device" (one first-party cookie). */
  remember: boolean;
  env: SnapshotEnv;
  /** Time the page was open, and time it was visible and in use (or the timer was running). */
  openMs: number;
  engagedMs: number;
  clicks: number;
  /** Keyboard shortcuts used. */
  keys: number;
  /** Which controls were clicked — by control id, never by text. */
  clicksBy: Record<string, number>;
  /** Feature usage counters (preset chosen, ambient sound, zen, exports …). */
  feats: Record<string, number>;
  focusStarted: number;
  focusDone: number;
  focusMin: number;
  breaksDone: number;
  interruptions: number;
  tasksAdded: number;
  tasksDone: number;
  errors: number;
  /** Core Web Vitals of this page load (ms, unitless for CLS). */
  vitals: { lcp: number; cls: number; inp: number };
}

const KEY_RE = /^[a-z0-9_.:-]{1,32}$/i;
const MAX_MAP_KEYS = 48;
const DAY_MS = 86_400_000;

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown, max: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(0, Math.round(v))) : 0;
const str = (v: unknown, re: RegExp, max: number): string =>
  typeof v === 'string' && v.length <= max && re.test(v) ? v : '';

function counts(raw: unknown, max = 10_000): Record<string, number> {
  const out: Record<string, number> = {};
  if (!isObj(raw)) return out;
  let n = 0;
  for (const [k, v] of Object.entries(raw)) {
    if (n >= MAX_MAP_KEYS) break;
    const c = num(v, max);
    if (!KEY_RE.test(k) || !c) continue;
    out[k] = c;
    n++;
  }
  return out;
}

export function sanitizeEnv(raw: unknown): SnapshotEnv {
  const e = isObj(raw) ? raw : {};
  return {
    lang: str(e.lang, /^[a-z]{2,3}$/, 3),
    screen: str(e.screen, /^[≤>]\d{3,4}$/, 6),
    theme: e.theme === 'light' || e.theme === 'dark' ? e.theme : '',
    standalone: e.standalone === true,
    ref: str(e.ref, /^[a-z0-9.-]{1,100}$/, 100),
    utm: str(e.utm, /^[a-z0-9_.-]{1,40}$/i, 40),
  };
}

/** The server-side gate for incoming snapshots. Returns null for anything that isn't one. */
export function sanitizeSnapshot(raw: unknown): Snapshot | null {
  if (!isObj(raw) || raw.v !== 1) return null;
  const sid = str(raw.sid, /^[a-f0-9-]{16,36}$/, 36);
  if (!sid) return null;
  const vit = isObj(raw.vitals) ? raw.vitals : {};
  return {
    v: 1,
    sid,
    remember: raw.remember === true,
    env: sanitizeEnv(raw.env),
    openMs: num(raw.openMs, DAY_MS),
    engagedMs: Math.min(num(raw.engagedMs, DAY_MS), num(raw.openMs, DAY_MS)),
    clicks: num(raw.clicks, 100_000),
    keys: num(raw.keys, 100_000),
    clicksBy: counts(raw.clicksBy),
    feats: counts(raw.feats),
    focusStarted: num(raw.focusStarted, 500),
    focusDone: num(raw.focusDone, 500),
    focusMin: num(raw.focusMin, 24 * 60),
    breaksDone: num(raw.breaksDone, 500),
    interruptions: num(raw.interruptions, 10_000),
    tasksAdded: num(raw.tasksAdded, 1_000),
    tasksDone: num(raw.tasksDone, 1_000),
    errors: num(raw.errors, 1_000),
    vitals: {
      lcp: num(vit.lcp, 120_000),
      cls: typeof vit.cls === 'number' && Number.isFinite(vit.cls) ? Math.min(10, Math.max(0, vit.cls)) : 0,
      inp: num(vit.inp, 60_000),
    },
  };
}

/* ---------- context helpers (pure) ---------- */

export function screenBucket(width: number): string {
  for (const w of [480, 768, 1024, 1440]) if (width <= w) return `≤${w}`;
  return '>1440';
}

/** Referrer → bare domain, or '' when it's empty, invalid or this site itself. */
export function refDomain(referrer: string, ownHost: string): string {
  if (!referrer) return '';
  try {
    const h = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
    return h === ownHost.toLowerCase().replace(/^www\./, '') ? '' : h.slice(0, 100);
  } catch {
    return '';
  }
}

export interface UAInfo {
  browser: string;
  os: string;
  device: 'mobile' | 'tablet' | 'desktop';
  bot: boolean;
}

/** Coarse user-agent parsing: families only (no versions), plus a bot check. */
export function parseUA(ua: string): UAInfo {
  const u = ua || '';
  const bot = /bot|crawl|spider|slurp|preview|headless|lighthouse|monitor|curl|wget|python|httpclient/i.test(
    u,
  );
  const os = /windows nt/i.test(u)
    ? 'Windows'
    : /iphone|ipad|ipod/i.test(u)
      ? 'iOS'
      : /android/i.test(u)
        ? 'Android'
        : /cros/i.test(u)
          ? 'ChromeOS'
          : /mac os x|macintosh/i.test(u)
            ? 'macOS'
            : /linux/i.test(u)
              ? 'Linux'
              : 'Other';
  const browser = /edg\//i.test(u)
    ? 'Edge'
    : /opr\/|opera/i.test(u)
      ? 'Opera'
      : /samsungbrowser/i.test(u)
        ? 'Samsung'
        : /firefox|fxios/i.test(u)
          ? 'Firefox'
          : /chrome|crios|chromium/i.test(u)
            ? 'Chrome'
            : /safari/i.test(u)
              ? 'Safari'
              : 'Other';
  const device = /ipad|tablet|(android(?!.*mobile))/i.test(u)
    ? 'tablet'
    : /mobi|iphone|ipod/i.test(u)
      ? 'mobile'
      : 'desktop';
  return { browser, os, device, bot };
}

/** Visit-frequency buckets for the dashboard (how many visits a remembered visitor has made). */
export function visitBucket(visits: number): string {
  if (visits <= 1) return '1';
  if (visits === 2) return '2';
  if (visits <= 5) return '3–5';
  if (visits <= 10) return '6–10';
  if (visits <= 30) return '11–30';
  return '31+';
}

/* ---------- the visitor's choices (stored on their device, never sent as an id) ---------- */

export interface PrivacyChoice {
  /** Anonymous usage stats at all (default on; off sends nothing). */
  stats: boolean;
  /** "Remember this device": ask once, then yes/no. */
  remember: 'ask' | 'yes' | 'no';
}

export const DEFAULT_PRIVACY: PrivacyChoice = { stats: true, remember: 'ask' };

export function sanitizePrivacy(raw: unknown): PrivacyChoice {
  const r = isObj(raw) ? raw : {};
  return {
    stats: typeof r.stats === 'boolean' ? r.stats : DEFAULT_PRIVACY.stats,
    remember: r.remember === 'yes' || r.remember === 'no' ? r.remember : 'ask',
  };
}

/** Control ids the click counter may report (anything else is grouped as `other`). */
export const controlName = (id: string): string => (KEY_RE.test(id) ? id : 'other');

/* ---------- what the dashboard receives (shared with the server) ---------- */

/** Whitelisted columns (safe to interpolate into SQL). */
export const BREAKDOWNS = [
  'country',
  'device',
  'browser',
  'os',
  'lang',
  'screen',
  'theme',
  'ref',
  'utm',
] as const;

/** Everything the /admin dashboard shows, aggregated over the last `days` days (UTC). */
export interface Stats {
  range: { days: number; from: string; to: string };
  totals: {
    visits: number;
    visitors: number;
    engagedMs: number;
    openMs: number;
    clicks: number;
    keys: number;
    focusStarted: number;
    focusDone: number;
    focusMin: number;
    breaksDone: number;
    interruptions: number;
    tasksAdded: number;
    tasksDone: number;
    errors: number;
    installed: number;
    rememberedVisits: number;
    newRemembered: number;
    rememberedVisitors: number;
  };
  daily: { day: string; visits: number; visitors: number; engagedMs: number; focusMin: number }[];
  breakdowns: Record<(typeof BREAKDOWNS)[number], { k: string; visits: number; visitors: number }[]>;
  hours: { k: number; visits: number }[];
  lengths: { k: string; visits: number }[];
  frequency: { k: string; visitors: number }[];
  clicks: { k: string; n: number }[];
  feats: { k: string; n: number }[];
  vitals: { lcp: number; cls: number; inp: number; samples: number };
  /** The same headline totals for the equally long period just before this one (change arrows). */
  prev: PeriodTotals;
  /** Every country with visits in the range, most visits first (the map and its detail panel). */
  countries: CountryStats[];
}

export type PeriodTotals = Pick<
  Stats['totals'],
  'visits' | 'visitors' | 'engagedMs' | 'clicks' | 'focusStarted' | 'focusDone' | 'focusMin'
>;

export interface Share {
  k: string;
  n: number;
}

export interface CountryStats {
  /** ISO 3166-1 alpha-2, or `XX` when unknown. */
  cc: string;
  visits: number;
  visitors: number;
  engagedMs: number;
  clicks: number;
  focusDone: number;
  focusMin: number;
  /** Visits from remembered visitors, and how many of those were return visits. */
  remembered: number;
  returnVisits: number;
  lastDay: string;
  devices: Share[];
  browsers: Share[];
  os: Share[];
  langs: Share[];
  /** Visits by UTC hour, 24 entries. */
  hours: number[];
}
